import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  AuditLogExportScopes,
  downloadAuditLogsCsv,
  filterAuditLogsByChangeType,
  filterAuditLogsByKey,
  filterAuditLogsBySearch,
  getAuditLogChangeFilterCounts,
  RydeUiUnit,
  SEARCH_TYPING_DEBOUNCE_MS,
  TAuditLogChangeFilter,
  TAuditLogExportScope,
  toggleArrayItem,
  toggleSetItem,
  useDebouncedValue,
  useFormats,
} from "@ryde/shared";

import type { IAuditLogContext } from "../../contexts/audit-log.context";
import { useAuditLog } from "../use-audit-log";
import { translateEntityPropertyKey } from "../use-entity-property-key-normalized.utils";

/** One entity's audit history as a drawer shows it: fetched, searched, filtered by kind of change, rows opened and closed. */
export const useAuditLogHistory = ({
  id,
  entity,
}: {
  id: number;
  entity: RydeUiUnit;
}) => {
  const { t, i18n } = useTranslation();
  const { formatDateTimeWithSecondsNormalized } = useFormats();
  const { fetchAuditLogById, auditLogs, loading, error } = useAuditLog(entity);
  // The boxes show what is typed at once; the history is narrowed once typing pauses.
  const [searchInput, setSearchInput] = useState("");
  const [keyInput, setKeyInput] = useState("");
  const searchValue = useDebouncedValue({
    value: searchInput,
    delayMs: SEARCH_TYPING_DEBOUNCE_MS,
  });
  const keySearchValue = useDebouncedValue({
    value: keyInput,
    delayMs: SEARCH_TYPING_DEBOUNCE_MS,
  });
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [expandAllNested, setExpandAllNested] = useState(false);
  const [expandAllVersion, setExpandAllVersion] = useState(0);
  const [changeFilters, setChangeFilters] = useState<TAuditLogChangeFilter[]>(
    [],
  );

  const getKeyLabel = useMemo(
    () => (key: string) => translateEntityPropertyKey({ entity, key, t, i18n }),
    [entity, t, i18n],
  );

  const searchedAuditLogs = useMemo(
    () =>
      filterAuditLogsByKey({
        auditLogs: filterAuditLogsBySearch(auditLogs, searchValue),
        keySearch: keySearchValue,
        getKeyLabel,
      }),
    [auditLogs, searchValue, keySearchValue, getKeyLabel],
  );
  const filteredAuditLogs = useMemo(
    () =>
      filterAuditLogsByChangeType({
        auditLogs: searchedAuditLogs,
        filters: changeFilters,
      }),
    [searchedAuditLogs, changeFilters],
  );
  const changeFilterCounts = useMemo(
    () => getAuditLogChangeFilterCounts(searchedAuditLogs),
    [searchedAuditLogs],
  );

  // While searching, open the rows that still have matching fields; clearing the search closes them all.
  useEffect(() => {
    if (!searchValue.trim() && !keySearchValue.trim()) {
      setExpandedItems(new Set());
      return;
    }
    setExpandedItems(
      new Set(
        filteredAuditLogs
          .filter(log => log.entityProperties?.length)
          .map(log => log.id),
      ),
    );
  }, [filteredAuditLogs, searchValue, keySearchValue]);

  const refresh = useEvent(() => {
    void fetchAuditLogById(id);
  });

  useEffect(() => {
    void fetchAuditLogById(id);
  }, [fetchAuditLogById, id]);

  const onToggleExpanded = useEvent((itemId: number) => {
    setExpandedItems(items => toggleSetItem({ items, item: itemId }));
  });

  const setAllExpanded = (isExpanded: boolean): void => {
    setExpandedItems(
      new Set(isExpanded ? filteredAuditLogs.map(log => log.id) : []),
    );
    setExpandAllNested(isExpanded);
    setExpandAllVersion(version => version + 1);
  };
  const onExpandAll = useEvent(() => setAllExpanded(true));
  const onCollapseAll = useEvent(() => setAllExpanded(false));

  const onToggleChangeFilter = useEvent((filter: TAuditLogChangeFilter) => {
    setChangeFilters(items => toggleArrayItem({ items, item: filter }));
  });

  const onExport = useEvent((scope: TAuditLogExportScope) => {
    downloadAuditLogsCsv(
      scope === AuditLogExportScopes.all ? auditLogs : filteredAuditLogs,
      formatDateTimeWithSecondsNormalized,
      undefined,
      t,
    );
  });

  const contextValue = useMemo(
    () =>
      ({
        searchValue,
        keySearchValue,
        showSearchResultsOnly: false,
        expandedItems,
        onToggleExpanded,
        expandAllNested,
        expandAllVersion,
      }) satisfies IAuditLogContext,
    [
      searchValue,
      keySearchValue,
      expandedItems,
      onToggleExpanded,
      expandAllNested,
      expandAllVersion,
    ],
  );

  return {
    auditLogs,
    filteredAuditLogs,
    loading,
    error,
    contextValue,
    searchSectionProps: {
      searchValue: searchInput,
      onSearchType: setSearchInput,
      onSearchApply: setSearchInput,
      keySearchValue: keyInput,
      onKeySearchType: setKeyInput,
      onExpandAll,
      onCollapseAll,
      changeFilters,
      changeFilterCounts,
      onToggleChangeFilter,
      shownCount: filteredAuditLogs.length,
      totalCount: auditLogs.length,
      onExport,
      onRefresh: refresh,
    },
  };
};
